import { useState, useEffect, useCallback } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Ambulance,
  Pill,
  Stethoscope,
  Users,
  FolderOpen,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Activity,
  FileText,
  Upload,
  Printer,
  Heart,
  Bed,
} from "lucide-react";
import { useSanitarioAuth, SANITARIO_OSPITE_KEY } from "../../context/SanitarioAuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function calcEta(dataNascita) {
  if (!dataNascita) return null;
  const d = new Date(dataNascita);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age;
}

function formatData(s) {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleDateString("it-IT");
}

function listPreview(items, max = 3) {
  const arr = (items || []).map((x) => String(x).trim()).filter(Boolean);
  if (arr.length === 0) return "Nessuna";
  const shown = arr.slice(0, max).join(", ");
  return arr.length > max ? `${shown}…` : shown;
}

export default function SanitarioDashboard() {
  const { token, activeOperatore } = useSanitarioAuth();
  const outlet = useOutletContext() || {};
  const navigate = useNavigate();
  const [ospiteId, setOspiteId] = useState(
    () => localStorage.getItem(SANITARIO_OSPITE_KEY) || ""
  );
  const [fascicolo, setFascicolo] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [somministrazioni, setSomministrazioni] = useState([]);
  const [loadingOspite, setLoadingOspite] = useState(false);

  const loadAlerts = useCallback(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/oss/me/terapie-extra/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setAlerts(data.items || []))
      .catch(() => setAlerts([]));
  }, [token]);

  useEffect(() => {
    loadAlerts();
    const id = setInterval(loadAlerts, 60000);
    return () => clearInterval(id);
  }, [loadAlerts]);

  useEffect(() => {
    if (!token) return;
    if (!ospiteId) {
      // Primo ospite in elenco come contesto default
      axios
        .get(`${API_URL}/api/oss/me/fascicoli`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(({ data }) => {
          const first = (data || [])[0];
          if (first?.id) {
            setOspiteId(first.id);
            localStorage.setItem(SANITARIO_OSPITE_KEY, first.id);
          }
        })
        .catch(() => {});
      return;
    }

    setLoadingOspite(true);
    axios
      .get(`${API_URL}/api/oss/me/fascicoli/${ospiteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setFascicolo(data))
      .catch(() => setFascicolo(null))
      .finally(() => setLoadingOspite(false));

    axios
      .get(`${API_URL}/api/oss/me/fascicoli/${ospiteId}/terapie-extra/alerts`, {
        params: { scope: "today" },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        const items = data.items || [];
        const done = items
          .filter((i) => i.administered || i.eseguita || i.status === "eseguita")
          .slice(0, 5);
        setSomministrazioni(
          done.length > 0
            ? done
            : items.filter((i) => i.administered_at || i.somministrata_at).slice(0, 5)
        );
      })
      .catch(() => setSomministrazioni([]));
  }, [token, ospiteId]);

  const anag = fascicolo?.anagrafica || {};
  const san = fascicolo?.sanitaria || {};
  const nome = `${anag.cognome || fascicolo?.cognome || ""} ${anag.nome || fascicolo?.nome || ""}`.trim();
  const eta = calcEta(anag.data_nascita || fascicolo?.data_nascita);
  const patologie = [
    ...(san.diagnosi_principale ? [san.diagnosi_principale] : []),
    ...(san.patologie_croniche || []),
    ...(san.diagnosi_secondarie || []),
  ];
  const terapieCount = (fascicolo?.terapie_in_corso || []).length || (san.terapie || []).length || 0;
  const medico = san.medico_curante || "—";

  const blocks = [
    {
      key: "118",
      title: "Pulsante Emergenza",
      desc: "Attiva emergenza e genera scheda sanitaria completa.",
      btn: "Attiva 118",
      icon: Ambulance,
      accent: "from-red-500 to-red-700",
      border: "border-red-200",
      btnClass: "bg-red-600 hover:bg-red-700 text-white",
      watermark: "118",
      onClick: () => outlet.open118?.(),
    },
    {
      key: "terapie",
      title: "Deblistering e Terapie",
      desc: "Gestisci terapie, somministrazioni e controlla i farmaci.",
      btn: "Terapie e farmaci",
      icon: Pill,
      accent: "from-sky-400 to-sky-600",
      border: "border-sky-200",
      btnClass: "bg-sky-600 hover:bg-sky-700 text-white",
      to: "/sanitario/terapie",
    },
    {
      key: "medici",
      title: "Comunicazione Medici",
      desc: "Invia aggiornamenti, richieste e consulta risposte.",
      btn: "Apri comunicazioni",
      icon: Stethoscope,
      accent: "from-emerald-400 to-emerald-600",
      border: "border-emerald-200",
      btnClass: "bg-emerald-600 hover:bg-emerald-700 text-white",
      to: "/sanitario/comunicazioni/medici",
    },
    {
      key: "parenti",
      title: "Comunicazione Parenti",
      desc: "Condividi aggiornamenti e documenti in modo sicuro.",
      btn: "Apri comunicazioni",
      icon: Users,
      accent: "from-amber-400 to-orange-500",
      border: "border-amber-200",
      btnClass: "bg-amber-500 hover:bg-amber-600 text-white",
      to: "/sanitario/comunicazioni/parenti",
    },
    {
      key: "scheda",
      title: "Scheda Sanitaria",
      desc: "Visualizza dati clinici, parametri, documenti e diario sanitario.",
      btn: "Apri scheda",
      icon: FolderOpen,
      accent: "from-violet-500 to-purple-700",
      border: "border-violet-200",
      btnClass: "bg-violet-600 hover:bg-violet-700 text-white",
      to: ospiteId ? `/sanitario/scheda/${ospiteId}` : "/sanitario/scheda",
    },
  ];

  const quickActions = [
    {
      label: "Nuova rilevazione",
      icon: Activity,
      onClick: () =>
        navigate(ospiteId ? `/sanitario/scheda/${ospiteId}` : "/sanitario/scheda"),
    },
    {
      label: "Nuova nota clinica",
      icon: FileText,
      onClick: () => navigate("/sanitario/comunicazioni/medici"),
    },
    {
      label: "Carica documento",
      icon: Upload,
      onClick: () =>
        navigate(ospiteId ? `/sanitario/scheda/${ospiteId}` : "/sanitario/scheda"),
    },
    {
      label: "Stampa scheda emergenza",
      icon: Printer,
      onClick: () => outlet.open118?.(),
    },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      {/* Contesto ospite */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5">
        {loadingOspite && !fascicolo ? (
          <div className="h-24 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : fascicolo ? (
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {anag.foto_url ? (
                <img
                  src={anag.foto_url}
                  alt=""
                  className="w-16 h-16 rounded-full object-cover border-2 border-slate-200 shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <Heart className="w-7 h-7 text-slate-400" />
                </div>
              )}
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-slate-900 truncate">
                  {nome || "Ospite"}
                </h2>
                <p className="text-sm text-slate-500">
                  Nato/a {formatData(anag.data_nascita)}
                  {eta != null ? ` (${eta} anni)` : ""}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <Bed className="w-3.5 h-3.5" />
                  Camera {fascicolo.camera || "—"}
                  <span className="text-slate-300">·</span>
                  Medico: {medico}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 flex-1">
              <div className="rounded-xl bg-red-50 border border-red-100 px-3 py-2.5">
                <p className="text-[11px] font-semibold text-red-700 uppercase tracking-wide">
                  Allergie
                </p>
                <p className="text-sm text-slate-800 mt-0.5 leading-snug">
                  {listPreview(san.allergie)}
                </p>
              </div>
              <div className="rounded-xl bg-sky-50 border border-sky-100 px-3 py-2.5">
                <p className="text-[11px] font-semibold text-sky-700 uppercase tracking-wide">
                  Patologie
                </p>
                <p className="text-sm text-slate-800 mt-0.5 leading-snug">
                  {listPreview(patologie)}
                </p>
              </div>
              <div className="rounded-xl bg-violet-50 border border-violet-100 px-3 py-2.5">
                <p className="text-[11px] font-semibold text-violet-700 uppercase tracking-wide">
                  Terapie attive
                </p>
                <p className="text-sm text-slate-800 mt-0.5">
                  {terapieCount} {terapieCount === 1 ? "Farmaco" : "Farmaci"}
                </p>
              </div>
            </div>

            <Link
              to={`/sanitario/scheda/${ospiteId}`}
              className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-[#C83048] text-white text-sm font-semibold px-4 py-3 hover:bg-[#A8283C] transition touch-manipulation"
            >
              Vai alla scheda
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-slate-800">Nessun ospite selezionato</p>
              <p className="text-sm text-slate-500">
                Scegli un ospite per vedere allergie, patologie e terapie in evidenza.
              </p>
            </div>
            <Link
              to="/sanitario/scheda"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-[#C83048] hover:bg-slate-50"
            >
              Seleziona ospite
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </section>

      {/* Griglia moduli */}
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {blocks.map((b) => {
          const Icon = b.icon;
          const inner = (
            <>
              <div className="relative z-10 flex items-start gap-3">
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${b.accent} flex items-center justify-center text-white shadow-sm shrink-0`}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-900">{b.title}</h3>
                  <p className="text-sm text-slate-500 mt-1 leading-snug">{b.desc}</p>
                </div>
              </div>
              {b.watermark && (
                <span className="absolute right-3 top-2 text-5xl font-black text-red-100 select-none pointer-events-none">
                  {b.watermark}
                </span>
              )}
              <div className="relative z-10 mt-4">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold ${b.btnClass}`}
                >
                  {b.btn}
                </span>
              </div>
            </>
          );

          const className = `relative overflow-hidden text-left bg-white rounded-2xl border ${b.border} shadow-sm p-5 hover:shadow-md transition touch-manipulation`;

          if (b.to) {
            return (
              <Link key={b.key} to={b.to} className={className}>
                {inner}
              </Link>
            );
          }
          return (
            <button key={b.key} type="button" onClick={b.onClick} className={className}>
              {inner}
            </button>
          );
        })}
      </section>

      {/* Riga inferiore */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <h3 className="font-bold text-slate-800 mb-3">Promemoria e Alert</h3>
          <div className="space-y-2.5">
            {alerts.length === 0 ? (
              <p className="text-sm text-slate-500">Nessun alert attivo.</p>
            ) : (
              alerts.slice(0, 4).map((a, i) => (
                <div
                  key={a.id || a.terapia_extra_id || i}
                  className="flex items-start gap-2.5 rounded-xl bg-slate-50 px-3 py-2.5"
                >
                  {a.missing || a.mancante ? (
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  ) : (
                    <Pill className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {a.farmaco || a.nome || a.ospite_nome || "Terapia"}
                    </p>
                    <p className="text-xs text-slate-500">
                      {a.ospite_nome || a.nome_completo || ""}
                      {a.scheduled_at
                        ? ` · ${new Date(a.scheduled_at).toLocaleTimeString("it-IT", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}`
                        : ""}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          <Link
            to="/sanitario/terapie"
            className="mt-3 inline-flex text-sm font-medium text-sky-700 hover:underline"
          >
            Vedi tutti gli alert
          </Link>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <h3 className="font-bold text-slate-800 mb-3">Ultime Somministrazioni</h3>
          <div className="space-y-2.5">
            {somministrazioni.length === 0 ? (
              <p className="text-sm text-slate-500">
                Nessuna somministrazione recente per l&apos;ospite in contesto.
              </p>
            ) : (
              somministrazioni.map((s, i) => (
                <div
                  key={s.id || i}
                  className="flex items-start gap-2.5 rounded-xl bg-slate-50 px-3 py-2.5"
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {s.farmaco || s.nome || "Terapia"}
                    </p>
                    <p className="text-xs text-slate-500">
                      Eseguita
                      {s.administered_at || s.somministrata_at
                        ? ` · ${new Date(
                            s.administered_at || s.somministrata_at
                          ).toLocaleString("it-IT", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}`
                        : ""}
                      {activeOperatore
                        ? ` · ${activeOperatore.nome} ${activeOperatore.cognome?.[0]}.`
                        : ""}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          <Link
            to={ospiteId ? `/sanitario/scheda/${ospiteId}` : "/sanitario/terapie"}
            className="mt-3 inline-flex text-sm font-medium text-sky-700 hover:underline"
          >
            Vai a storico terapie →
          </Link>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <h3 className="font-bold text-slate-800 mb-3">Azioni Rapide</h3>
          <div className="grid grid-cols-2 gap-2">
            {quickActions.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.label}
                  type="button"
                  onClick={a.onClick}
                  className="flex flex-col items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50/40 hover:bg-sky-50 px-2 py-4 text-center touch-manipulation transition"
                >
                  <Icon className="w-5 h-5 text-sky-700" />
                  <span className="text-xs font-semibold text-slate-700 leading-tight">
                    {a.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
